/**
 * OnlineConfigPanel — SuperAdmin: qruplar → VLAN-lar əlaqəsi.
 * Sistem → Online Konfiqurasiyası.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Wifi, X, Search, RefreshCw, AlertTriangle, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OnlineConfigPanel() {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [groups, setGroups] = useState([]);
  const [sysGroups, setSysGroups] = useState([]);
  const [zabbixItems, setZabbixItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/network/groups`, { headers }),
        axios.get(`${API}/groups`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/network/zabbix/items`, { headers }),
      ]);
      setGroups(r1.data || []);
      setSysGroups(r2.data || []);
      setZabbixItems(r3.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Yükləmə uğursuz');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async (form) => {
    try {
      if (form.id) {
        const { id, ...payload } = form;
        await axios.put(`${API}/network/groups/${id}`, payload, { headers });
        toast.success('Yeniləndi');
      } else {
        await axios.post(`${API}/network/groups`, form, { headers });
        toast.success('Əlavə edildi');
      }
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Saxlanılmadı');
    }
  };

  const handleDelete = async (g) => {
    if (!window.confirm(`«${g.name}» qrupu silinsin?`)) return;
    try {
      await axios.delete(`${API}/network/groups/${g.id}`, { headers });
      toast.success('Silindi');
      fetchAll();
    } catch (e) {
      toast.error('Silinmədi');
    }
  };

  return (
    <div className="space-y-4" data-testid="online-config-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Wifi className="h-5 w-5 text-purple-600" />
            Online Konfiqurasiyası
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Zabbix VLAN-larını sistemin qruplarına təyin et — Group Admin-lər həmin grafikləri görəcək
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchAll} size="sm">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            onClick={() => setEditing({ __new: true })}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="add-net-group-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Yeni qrup
          </Button>
        </div>
      </div>

      <div className="text-xs text-zinc-500 bg-blue-50 border border-blue-200 rounded p-2">
        ℹ️ Zabbix-də cəmi <b>{zabbixItems.length}</b> VLAN item-i mövcuddur. Bu sayda VLAN-ı qruplara
        bölərək hər qrup admininin yalnız öz ərazisindəki abunəçi statistikasını görməsini təmin edirik.
      </div>

      <AnomalyConfigSection headers={headers} />

      {groups.length === 0 && !loading && (
        <div className="border-2 border-dashed rounded-lg p-12 text-center">
          <Wifi className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
          <div className="text-sm text-zinc-500">Heç bir əlaqə qurulmayıb</div>
        </div>
      )}

      <div className="space-y-2">
        {groups.map((g) => (
          <div
            key={g.id}
            className={`border rounded-lg p-3 ${g.active ? 'bg-white' : 'bg-zinc-50 opacity-60'}`}
            data-testid={`net-group-${g.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{g.name}</span>
                  <Badge className={g.active ? 'bg-emerald-100 text-emerald-700 text-[10px]' : 'bg-zinc-200 text-zinc-600 text-[10px]'}>
                    {g.active ? '● Aktiv' : 'Deaktiv'}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(g.zabbix_items || []).slice(0, 8).map((it) => (
                    <Badge key={it.zabbix_item_id} variant="secondary" className="text-[10px]">
                      {it.display_name} {it.vlan ? `(${it.vlan})` : ''}
                    </Badge>
                  ))}
                  {(g.zabbix_items || []).length > 8 && (
                    <Badge variant="secondary" className="text-[10px]">+{g.zabbix_items.length - 8}</Badge>
                  )}
                  {(g.zabbix_items || []).length === 0 && (
                    <span className="text-[10px] text-zinc-400 italic">VLAN seçilməyib</span>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => setEditing({ ...g })} data-testid={`edit-${g.id}`}>
                  <Edit2 className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(g)} data-testid={`delete-${g.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EditDialog
          group={editing}
          sysGroups={sysGroups}
          zabbixItems={zabbixItems}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function EditDialog({ group, sysGroups, zabbixItems, onClose, onSave }) {
  const [form, setForm] = useState({
    id: group.__new ? null : group.id,
    group_id: group.group_id || '',
    name: group.name || '',
    zabbix_items: group.zabbix_items || [],
    active: group.active === undefined ? true : group.active,
  });
  const [search, setSearch] = useState('');

  const selectedIds = new Set((form.zabbix_items || []).map((i) => i.zabbix_item_id));

  const toggleItem = (z) => {
    if (selectedIds.has(z.item_id)) {
      setForm({ ...form, zabbix_items: form.zabbix_items.filter((i) => i.zabbix_item_id !== z.item_id) });
    } else {
      setForm({
        ...form,
        zabbix_items: [
          ...(form.zabbix_items || []),
          {
            vlan: z.vlan,
            zabbix_item_id: z.item_id,
            zabbix_key: z.key,
            display_name: z.name.replace(' Summary Sessions', '').replace(' Sessions', '').trim(),
          },
        ],
      });
    }
  };

  const filteredItems = zabbixItems.filter((z) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      z.name.toLowerCase().includes(q)
      || String(z.vlan || '').includes(q)
      || (z.host_name || '').toLowerCase().includes(q)
    );
  });

  const handleSubmit = () => {
    if (!form.name.trim()) { toast.error('Ad lazımdır'); return; }
    if (!form.group_id) { toast.error('Sistem qrupu seçin'); return; }
    onSave(form);
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="net-group-edit-dialog">
        <DialogHeader>
          <DialogTitle>{form.id ? 'Online qrupu redaktə' : 'Yeni Online qrupu'}</DialogTitle>
          <DialogDescription className="sr-only">
            Sistem qrupu seçin və ona aid VLAN-ları işarələyin.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 flex-1 overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Sistem qrupu *</label>
              <Select value={form.group_id} onValueChange={(v) => {
                const sg = sysGroups.find((g) => g.id === v);
                setForm({ ...form, group_id: v, name: form.name || sg?.name || '' });
              }}>
                <SelectTrigger data-testid="sys-group-select"><SelectValue placeholder="Seç..." /></SelectTrigger>
                <SelectContent>
                  {sysGroups.length === 0 && <SelectItem value="__empty" disabled>Heç bir qrup yoxdur</SelectItem>}
                  {sysGroups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Göstərmə adı *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="məs. Yasamal"
                data-testid="net-group-name"
              />
            </div>
          </div>

          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">
                VLAN-lar (seçilmiş: {form.zabbix_items?.length || 0} / {zabbixItems.length})
              </label>
            </div>
            <div className="relative mb-2">
              <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-zinc-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Ad, VLAN ID və ya host axtar..."
                className="pl-7 h-8 text-sm"
                data-testid="vlan-search"
              />
            </div>
            <div className="border rounded-md p-2 max-h-[40vh] overflow-y-auto space-y-1">
              {filteredItems.length === 0 && <div className="text-xs text-zinc-400 italic p-2">Heç nə tapılmadı</div>}
              {filteredItems.map((z) => {
                const selected = selectedIds.has(z.item_id);
                return (
                  <label
                    key={z.item_id}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-zinc-50 ${selected ? 'bg-purple-50' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleItem(z)}
                      data-testid={`vlan-checkbox-${z.item_id}`}
                    />
                    <span className="text-[10px] font-mono text-zinc-400 w-12">VLAN {z.vlan || '?'}</span>
                    <span className="text-sm flex-1 truncate" title={z.name}>
                      {z.name.replace(' Summary Sessions', '').replace(' Sessions', '').trim()}
                    </span>
                    <span className="text-[10px] text-zinc-500">{z.host_name}</span>
                    {z.last_value !== null && (
                      <Badge variant="secondary" className="text-[10px]">{z.last_value}</Badge>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2 border-t">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              data-testid="net-group-active"
            />
            <span className="text-sm">Aktiv (statistikalar çəkilsin)</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-1" /> Ləğv
          </Button>
          <Button onClick={handleSubmit} className="bg-purple-600 hover:bg-purple-700" data-testid="net-group-save">
            Yadda saxla
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ==================== Anomaly Configuration Section ====================
function AnomalyConfigSection({ headers }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/network/anomaly-config`, { headers });
      setCfg(r.data);
    } catch (e) {
      toast.error('Anomaly konfiqurasiyası yüklənmədi');
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      // ensure numeric types
      const payload = {
        enabled: !!cfg.enabled,
        window_seconds: Number(cfg.window_seconds) || 300,
        drop_pct_threshold: Number(cfg.drop_pct_threshold) || 10,
        min_absolute_drop: Number(cfg.min_absolute_drop) || 20,
        abs_drop_threshold: Number(cfg.abs_drop_threshold) || 100,
        dedupe_seconds: Number(cfg.dedupe_seconds) || 3600,
        check_interval_seconds: Number(cfg.check_interval_seconds) || 300,
      };
      await axios.put(`${API}/network/anomaly-config`, payload, { headers });
      toast.success('Konfiqurasiya yeniləndi');
      setCfg({ ...cfg, ...payload });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Saxlanılmadı');
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return null;

  return (
    <div className="border rounded-lg bg-white" data-testid="anomaly-config-section">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-3 hover:bg-zinc-50 transition"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <span className="font-semibold text-sm">Anomaliya konfiqurasiyası</span>
          <Badge className={cfg.enabled ? 'bg-emerald-100 text-emerald-700 text-[10px]' : 'bg-zinc-200 text-zinc-600 text-[10px]'}>
            {cfg.enabled ? 'Aktiv' : 'Deaktiv'}
          </Badge>
        </div>
        <span className="text-xs text-zinc-500">
          {cfg.drop_pct_threshold}% və ya ≥{cfg.abs_drop_threshold} abunə düşüş • {Math.round(cfg.window_seconds / 60)} dəq pəncərə {expanded ? '▲' : '▼'}
        </span>
      </button>
      {expanded && (
        <div className="border-t p-3 space-y-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={cfg.enabled}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
              data-testid="anomaly-enabled"
            />
            <span className="text-sm">Aktivdir (fon prosesi anomaliyaları yoxlasın)</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <NumField
              label="Pəncərə (dəqiqə)"
              hint="Neçə dəqiqə əvvəl vs indi müqayisə edilsin"
              value={Math.round(cfg.window_seconds / 60)}
              onChange={(v) => setCfg({ ...cfg, window_seconds: Math.max(1, v) * 60 })}
              testid="anomaly-window"
              min={1} max={60}
            />
            <NumField
              label="Yoxlama tezliyi (dəqiqə)"
              hint="Fon prosesin işə düşmə intervalı"
              value={Math.round(cfg.check_interval_seconds / 60)}
              onChange={(v) => setCfg({ ...cfg, check_interval_seconds: Math.max(1, v) * 60 })}
              testid="anomaly-check-interval"
              min={1} max={60}
            />
            <NumField
              label="Dedupe (dəqiqə)"
              hint="Eyni qrup üçün təkrar xəbərdarlıq intervalı"
              value={Math.round(cfg.dedupe_seconds / 60)}
              onChange={(v) => setCfg({ ...cfg, dedupe_seconds: Math.max(1, v) * 60 })}
              testid="anomaly-dedupe"
              min={5} max={1440}
            />
          </div>

          <div className="border-t pt-3">
            <div className="text-xs font-medium text-zinc-600 mb-2">Tetikleyici şərtləri (OR məntiqi ilə):</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <NumField
                label="Faiz düşüşü %"
                hint="Bundan çox faiz düşüş → xəbərdarlıq"
                value={cfg.drop_pct_threshold}
                onChange={(v) => setCfg({ ...cfg, drop_pct_threshold: v })}
                testid="anomaly-pct-threshold"
                step="0.5" min={1} max={99}
              />
              <NumField
                label="Min mütləq düşüş (faiz üçün)"
                hint="Faiz tetiği yalnız bu qədər mütləq itki varsa aktivləşir (kiçik qruplarda səs-küy önləyir)"
                value={cfg.min_absolute_drop}
                onChange={(v) => setCfg({ ...cfg, min_absolute_drop: v })}
                testid="anomaly-min-abs"
                min={1} max={10000}
              />
              <NumField
                label="Mütləq düşüş həddi"
                hint="Bundan çox abunə düşərsə → xəbərdarlıq (%-dan asılı olmayaraq)"
                value={cfg.abs_drop_threshold}
                onChange={(v) => setCfg({ ...cfg, abs_drop_threshold: v })}
                testid="anomaly-abs-threshold"
                min={1} max={100000}
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t">
            <div className="text-[11px] text-zinc-500">
              Nümunə: <b>{cfg.drop_pct_threshold}%</b> faiz düşüşü <b>(≥{cfg.min_absolute_drop} abunə)</b>
              {' '}və ya <b>≥{cfg.abs_drop_threshold} abunə</b> mütləq düşüşü olduqda anomaliya kimi qeyd olunur.
            </div>
            <Button onClick={save} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="anomaly-save-btn">
              <Save className="h-3.5 w-3.5 mr-1" /> Saxla
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function NumField({ label, hint, value, onChange, testid, min, max, step }) {
  return (
    <div>
      <label className="text-xs font-medium">{label}</label>
      <Input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-8 text-sm"
        data-testid={testid}
      />
      {hint && <div className="text-[10px] text-zinc-400 mt-0.5">{hint}</div>}
    </div>
  );
}
